// Prospecting / Estimate Mode.
//
// First meetings rarely come with a census – prospects give rough numbers
// ("about 20 people, most make around $65k, two owners"). This module turns
// those rough inputs into a plausible synthetic census that runs through the
// exact same calculation engine as real data, so every feature of the app
// works while staying clearly labeled as an estimate.
//
// The generator is fully deterministic for a given seed: the same assumptions
// always produce the same census, so a saved estimate reloads identically.

export const COMP_SPREADS = {
  tight: { label: 'Mostly similar pay', sigma: 0.12 },
  moderate: { label: 'Some variation', sigma: 0.3 },
  wide: { label: 'Wide range', sigma: 0.55 },
}

export const AGE_MIXES = {
  younger: { label: 'Mostly younger (20s–30s)', mean: 33, sd: 7 },
  mixed: { label: 'Mixed ages', mean: 41, sd: 10 },
  older: { label: 'Mostly older (45+)', mean: 51, sd: 8 },
}

// mulberry32 – tiny, fast, deterministic PRNG.
export function createSeededRng(seed) {
  let a = seed >>> 0
  return function rng() {
    a |= 0
    a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
const toNumber = (value, fallback) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const ESTIMATOR_DEFAULTS = {
  headcount: 40,
  typicalComp: 65000,
  compSpread: 'wide',
  ownerCount: 3,
  ownerComp: 250000,
  ageMix: 'mixed',
  participationRate: 50,
  avgDeferralPercent: 3,
  // Optional detail – 0 means "don't model it", so a quick estimate needs
  // nothing beyond the basics above.
  hceCount: 2,
  partTimePercent: 0,
  turnoverPercent: 0,
}

export function normalizeEstimatorInputs(raw = {}) {
  const headcount = clamp(Math.round(toNumber(raw.headcount, ESTIMATOR_DEFAULTS.headcount)), 1, 2000)
  return {
    headcount,
    typicalComp: clamp(Math.round(toNumber(raw.typicalComp, ESTIMATOR_DEFAULTS.typicalComp)), 15000, 2000000),
    compSpread: COMP_SPREADS[raw.compSpread] ? raw.compSpread : ESTIMATOR_DEFAULTS.compSpread,
    ownerCount: clamp(Math.round(toNumber(raw.ownerCount, ESTIMATOR_DEFAULTS.ownerCount)), 0, Math.min(20, headcount)),
    ownerComp: clamp(Math.round(toNumber(raw.ownerComp, ESTIMATOR_DEFAULTS.ownerComp)), 15000, 5000000),
    ageMix: AGE_MIXES[raw.ageMix] ? raw.ageMix : ESTIMATOR_DEFAULTS.ageMix,
    participationRate: clamp(Math.round(toNumber(raw.participationRate, ESTIMATOR_DEFAULTS.participationRate)), 0, 100),
    avgDeferralPercent: clamp(toNumber(raw.avgDeferralPercent, ESTIMATOR_DEFAULTS.avgDeferralPercent), 0, 100),
    hceCount: clamp(Math.round(toNumber(raw.hceCount, ESTIMATOR_DEFAULTS.hceCount)), 0, headcount),
    partTimePercent: clamp(Math.round(toNumber(raw.partTimePercent, ESTIMATOR_DEFAULTS.partTimePercent)), 0, 100),
    turnoverPercent: clamp(Math.round(toNumber(raw.turnoverPercent, ESTIMATOR_DEFAULTS.turnoverPercent)), 0, 100),
    // The anchor year pins DOB/hire-date math so a saved estimate regenerates
    // identically later; the seed pins everything else.
    anchorYear: clamp(Math.round(toNumber(raw.anchorYear, new Date().getFullYear())), 2020, 2100),
    seed: Math.abs(Math.round(toNumber(raw.seed, Math.floor(Math.random() * 2 ** 31)))) || 1,
  }
}

// Human-readable one-liner for banners, Julio, and saved-session tooltips.
export function describeEstimatorInputs(inputs) {
  if (!inputs) return ''
  const parts = [
    `${inputs.headcount} employees`,
    `typical pay ~$${Number(inputs.typicalComp).toLocaleString()} (${COMP_SPREADS[inputs.compSpread]?.label.toLowerCase() || inputs.compSpread})`,
    inputs.ownerCount > 0 ? `${inputs.ownerCount} owner${inputs.ownerCount === 1 ? '' : 's'} at ~$${Number(inputs.ownerComp).toLocaleString()}` : 'no owners',
    AGE_MIXES[inputs.ageMix]?.label.toLowerCase() || inputs.ageMix,
    `~${inputs.participationRate}% participate at ~${inputs.avgDeferralPercent}%`,
    inputs.hceCount > 0 ? `${inputs.hceCount} non-owner HCE${inputs.hceCount === 1 ? '' : 's'}` : null,
    inputs.partTimePercent > 0 ? `~${inputs.partTimePercent}% part-time` : null,
    inputs.turnoverPercent > 0 ? `~${inputs.turnoverPercent}% turnover` : null,
  ].filter(Boolean)
  return parts.join(' · ')
}

export function generateSyntheticCensus(rawInputs) {
  const inputs = normalizeEstimatorInputs(rawInputs)
  const rng = createSeededRng(inputs.seed)
  // Box-Muller gaussian on top of the seeded uniform rng.
  const gauss = () => {
    const u = Math.max(rng(), 1e-9)
    const v = Math.max(rng(), 1e-9)
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
  }

  const spread = COMP_SPREADS[inputs.compSpread]
  const ages = AGE_MIXES[inputs.ageMix]
  const staffCount = inputs.headcount - inputs.ownerCount
  // Participation is a share of the entire census, including owners. Keep the
  // selected people eligible under the default plan so Results reflects this
  // estimate instead of silently lowering it.
  const participatingEmployees = Math.round((inputs.participationRate / 100) * inputs.headcount)
  // Optional detail, applied to deterministic index ranges of the staff:
  // HCE uplifts come first, part-timers from the end, and turnover from the
  // middle out (part-timers and terminated rows may overlap – realistic).
  const hceStaff = Math.min(inputs.hceCount, staffCount)
  const partTimeStaff = Math.round((inputs.partTimePercent / 100) * staffCount)
  const terminatedStaff = Math.round((inputs.turnoverPercent / 100) * staffCount)

  const pad = (n) => String(n).padStart(2, '0')
  const dateFor = (age) => {
    const year = inputs.anchorYear - age
    const month = 1 + Math.floor(rng() * 12)
    const day = 1 + Math.floor(rng() * 28)
    return `${year}-${pad(month)}-${pad(day)}`
  }

  const employees = []
  for (let i = 0; i < inputs.headcount; i += 1) {
    const isOwner = i < inputs.ownerCount
    const age = isOwner
      ? Math.round(clamp(ages.mean + 8 + gauss() * 6, 30, 67))
      : Math.round(clamp(ages.mean + gauss() * ages.sd, 21, 67))
    const dob = dateFor(age)

    const staffIndex = i - inputs.ownerCount
    const isHce = !isOwner && staffIndex < hceStaff
    const isPartTime = !isOwner && staffIndex >= staffCount - partTimeStaff
    const isTerminated = !isOwner && staffIndex >= Math.floor((staffCount - terminatedStaff) / 2) &&
      staffIndex < Math.floor((staffCount - terminatedStaff) / 2) + terminatedStaff

    let compensation = isOwner
      ? Math.round((inputs.ownerComp * (1 + gauss() * 0.05)) / 1000) * 1000
      : Math.max(15000, Math.round((inputs.typicalComp * Math.exp(gauss() * spread.sigma)) / 500) * 500)
    // Non-owner HCEs sit above the HCE threshold regardless of typical pay.
    if (isHce) compensation = Math.max(compensation, Math.round((170000 + Math.abs(gauss()) * 40000) / 1000) * 1000)
    const participates = i < participatingEmployees
    const generatedHours = isPartTime ? Math.round((700 + rng() * 700) / 10) * 10 : 2080
    const hoursServed = participates ? Math.max(1000, generatedHours) : generatedHours
    if (isPartTime) compensation = Math.max(15000, Math.round((compensation * (hoursServed / 2080)) / 500) * 500)

    // Tenure grows with age but stays plausible; owners tend to be founders.
    const maxTenure = Math.max(1, age - 21)
    const tenureYears = isOwner
      ? Math.min(maxTenure, Math.round(6 + rng() * 14))
      : Math.min(maxTenure, Math.max(1, Math.round(rng() * rng() * 18)))
    const hireDate = dateFor(tenureYears)

    const deferralPercent = participates
      ? Math.round(clamp((isOwner ? Math.max(inputs.avgDeferralPercent, 8) : inputs.avgDeferralPercent) + gauss() * 2, 1, 15) * 10) / 10
      : 0

    // Balance scales with what they defer and how long they've been in a plan.
    const balance = participates
      ? Math.round((compensation * (deferralPercent / 100) * Math.max(1, tenureYears) * 1.4) / 100) * 100
      : 0

    employees.push({
      id: `EMP${String(i + 1).padStart(3, '0')}`,
      name: '',
      dob,
      gender: rng() < 0.5 ? 'M' : 'F',
      hire_date: hireDate,
      tenure: '',
      termination_date: isTerminated
        ? `${inputs.anchorYear}-${pad(1 + Math.floor(rng() * 11))}-${pad(1 + Math.floor(rng() * 28))}`
        : '',
      hours_served: hoursServed,
      compensation,
      prior_year_compensation: Math.round((compensation * 0.96) / 100) * 100,
      // Fields the estimate doesn't model stay blank ("not provided"), not 0 –
      // a balance of 0 for a non-participant is the one real zero here.
      pretax_percent: participates ? deferralPercent : '',
      pretax_amount: '',
      roth_percent: '',
      roth_amount: '',
      balance,
      loan_balance: '',
      status: isTerminated ? 'Terminated' : 'Active',
      // Explicit Y/N on every row keeps top-heavy testing available.
      key_employee: isOwner ? 'Y' : 'N',
      five_percent_owner: isOwner ? 'Y' : 'N',
      allocation_group: isOwner ? 'Owner' : 'Staff',
      after_tax_percent: '',
      after_tax_amount: '',
    })
  }
  return employees
}
